import React, { useState, useEffect, useCallback } from 'react';
import { Info, X, Plus, Trash2, Globe, Lock, Mail, StickyNote, Link2, Eye, EyeOff, GripVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { id: 'url', label: 'Webbsida', icon: Globe, placeholder: 'https://...' },
  { id: 'test_url', label: 'Testmiljö', icon: Link2, placeholder: 'https://staging...' },
  { id: 'password', label: 'Lösenord', icon: Lock, placeholder: 'Inloggningsuppgifter' },
  { id: 'email', label: 'E-post', icon: Mail, placeholder: 'kontakt@...' },
  { id: 'note', label: 'Anteckning', icon: StickyNote, placeholder: 'Fritext...' },
];

function InfoField({ item, onChange, onRemove }) {
  const [showPassword, setShowPassword] = useState(false);
  const fieldType = FIELD_TYPES.find(f => f.id === item.type) || FIELD_TYPES[4];
  const Icon = fieldType.icon;

  return (
    <div className="group relative bg-[#09090B] border border-white/5 rounded-lg p-3 hover:border-white/10 transition-colors" data-testid={`info-field-${item.type}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-zinc-500" />
        <input
          value={item.label}
          onChange={(e) => onChange({ ...item, label: e.target.value })}
          className="text-xs font-medium text-zinc-400 bg-transparent border-none outline-none flex-1 placeholder:text-zinc-600"
          placeholder={fieldType.label}
          data-testid={`info-label-${item.type}`}
        />
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-all p-0.5"
          data-testid={`info-remove-${item.type}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      
      <div className="flex items-center gap-1.5">
        {item.type === 'note' ? (
          <textarea
            value={item.value}
            onChange={(e) => onChange({ ...item, value: e.target.value })}
            placeholder={fieldType.placeholder}
            rows={2}
            className="w-full bg-transparent text-sm text-zinc-200 border-none outline-none resize-none placeholder:text-zinc-700 leading-relaxed"
            data-testid={`info-value-${item.type}`}
          />
        ) : (
          <>
            <input
              type={item.type === 'password' && !showPassword ? 'password' : 'text'}
              value={item.value}
              onChange={(e) => onChange({ ...item, value: e.target.value })}
              placeholder={fieldType.placeholder}
              className="flex-1 bg-transparent text-sm text-zinc-200 border-none outline-none placeholder:text-zinc-700"
              data-testid={`info-value-${item.type}`}
            />
            {item.type === 'password' && item.value && (
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="text-zinc-600 hover:text-zinc-400 transition-colors"
                data-testid="info-toggle-password"
              >
                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            )}
            {(item.type === 'url' || item.type === 'test_url') && item.value && (
              <a
                href={item.value.startsWith('http') ? item.value : `https://${item.value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-yellow-400 transition-colors"
                data-testid="info-open-link"
              >
                <Globe className="w-3.5 h-3.5" />
              </a>
            )}
            {item.type === 'email' && item.value && (
              <a
                href={`mailto:${item.value}`}
                className="text-zinc-600 hover:text-yellow-400 transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function WorkspaceInfo() {
  const { currentWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [info, setInfo] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const fetchInfo = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      const { data } = await axios.get(`${API}/workspaces/${currentWorkspace.id}/info`, { withCredentials: true });
      setInfo(data.info || []);
      setHasChanges(false);
    } catch {
      // Silently fail
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (isOpen) fetchInfo();
  }, [isOpen, fetchInfo]);

  const handleSave = async () => {
    if (!currentWorkspace) return;
    setSaving(true);
    try {
      await axios.put(`${API}/workspaces/${currentWorkspace.id}/info`, { info }, { withCredentials: true });
      setHasChanges(false);
      toast.success('Info sparad');
    } catch {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const addField = (type) => {
    const fieldType = FIELD_TYPES.find(f => f.id === type);
    setInfo(prev => [...prev, { label: fieldType.label, value: '', type }]);
    setHasChanges(true);
    setShowAddMenu(false);
  };

  const updateField = (index, updated) => {
    setInfo(prev => prev.map((item, i) => i === index ? updated : item));
    setHasChanges(true);
  };

  const removeField = (index) => {
    setInfo(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const infoCount = info.filter(i => i.value).length;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="relative flex items-center justify-center w-7 h-7 rounded-md text-zinc-600 hover:text-zinc-300 hover:bg-white/5 transition-all"
        title="Workspace-info"
        data-testid="workspace-info-btn"
      >
        <Info className="w-4 h-4" />
        {infoCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-yellow-400 text-[7px] font-bold text-black flex items-center justify-center">
            {infoCount}
          </span>
        )}
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => {
            if (hasChanges) handleSave();
            setIsOpen(false);
          }}
          data-testid="workspace-info-backdrop"
        />
      )}

      {/* Slide-in panel */}
      <div
        className={`fixed top-0 right-0 h-full w-96 bg-[#111113] border-l border-white/5 z-50 transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="workspace-info-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div>
            <h2 className="text-sm font-semibold text-zinc-100 font-['Outfit']">
              Workspace-info
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {currentWorkspace?.name}
            </p>
          </div>
          <button
            onClick={() => {
              if (hasChanges) handleSave();
              setIsOpen(false);
            }}
            className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
            data-testid="workspace-info-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 120px)' }}>
          {info.length === 0 ? (
            <div className="text-center py-12">
              <Info className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-500 mb-1">Ingen info ännu</p>
              <p className="text-xs text-zinc-600">
                Lägg till länkar, lösenord och anteckningar för detta workspace
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {info.map((item, idx) => (
                <InfoField
                  key={idx}
                  item={item}
                  onChange={(updated) => updateField(idx, updated)}
                  onRemove={() => removeField(idx)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5 bg-[#111113]">
          <div className="flex items-center gap-2">
            {/* Add field menu */}
            <div className="relative flex-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddMenu(!showAddMenu)}
                className="w-full border-white/10 text-zinc-400 hover:bg-white/5 hover:text-zinc-100 h-8 text-xs"
                data-testid="workspace-info-add-btn"
              >
                <Plus className="w-3 h-3 mr-1.5" />
                Lägg till fält
              </Button>
              
              {showAddMenu && (
                <div className="absolute bottom-10 left-0 w-full bg-[#1a1a1e] border border-white/10 rounded-lg shadow-xl overflow-hidden z-10" data-testid="info-add-menu">
                  {FIELD_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => addField(type.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs text-zinc-300 hover:bg-white/5 transition-colors"
                      data-testid={`info-add-${type.id}`}
                    >
                      <type.icon className="w-3.5 h-3.5 text-zinc-500" />
                      {type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {hasChanges && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-yellow-400 text-black hover:bg-yellow-300 h-8 text-xs px-4"
                data-testid="workspace-info-save-btn"
              >
                {saving ? 'Sparar...' : 'Spara'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
